import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { isDesktopOffline } from '@/api/desktopSync';
import * as communitiesApi from '@/api/communities';
import { createAccessCode, updateAccessCode, deleteAccessCode } from '@/api/accessCodes';

export const COMMUNITY_WAIT_MS = 15000;
const connection = () => JSON.stringify([window.lvbDesktop?.backendUrl || null, window.lvbDesktop?.localBackendUrl || null]);
const principal = binding => binding?.user?.id && binding?.user?.tenant_id
  && ['cloud', 'offline'].includes(binding.source)
  ? JSON.stringify([binding.user.tenant_id, binding.user.id, binding.source]) : null;
const record = value => value && typeof value === 'object' && !Array.isArray(value);
const identified = value => record(value) && typeof value.id === 'string' && !!value.id;
const validList = value => Array.isArray(value) && value.every(row => identified(row) && typeof row.name === 'string');
const validDetail = (value, id) => identified(value) && value.id === id && typeof value.community_name === 'string'
  && Array.isArray(value.codes) && value.codes.every(identified)
  && Array.isArray(value.addresses) && value.addresses.every(row => record(row) && typeof row.place_id === 'string');
const invalidReceipt = () => { throw new Error('Unconfirmed community acknowledgement'); };
const pick = (value, keys) => Object.fromEntries(keys.filter(key => value[key] !== undefined).map(key => [key, value[key]]));

// Waiting is bounded even when an adapter ignores abort. This does not cancel a server write.
async function bounded(call, config) {
  let timer; let abort;
  const stopped = new Promise((_, reject) => {
    abort = () => reject(new Error('Community request stopped'));
    if (config.signal.aborted) { abort(); return; }
    config.signal.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => reject(new Error('Community request timed out')), COMMUNITY_WAIT_MS);
  });
  try { return await Promise.race([call(), stopped]); }
  finally { clearTimeout(timer); config.signal.removeEventListener('abort', abort); }
}

export function useCommunityWorkspace({ enabled = true, canManage = false } = {}) {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const [, render] = useState(0);
  // Reopening the section acquires authority; rerenders and replacement logins do not.
  const cycle = useMemo(() => ({ principal: principal(sessionBinding), revision: sessionBinding?.revision,
    source: sessionBinding?.source, connection: connection(), active: false, opening: 0,
    selected: null, detail: null, communities: [], listLoading: false, detailLoading: false,
    listError: '', detailError: '', listGeneration: 0, detailGeneration: 0, generation: 0,
    controllers: new Set(), dirty: new Map(), pendingPins: [], pinSequence: 0, busy: false,
    status: 'idle', message: '', recovery: null, batch: [], completedOperation: null, completion: 0,
    ticket: null, creationId: null,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [enabled]);
  const latest = useRef(null);
  latest.current = { sessionBinding, getToken, canManage, enabled };
  const renderedOpening = cycle.opening;
  const renderedSelection = cycle.selected;
  const owns = () => cycle.active && latest.current.enabled && !!cycle.principal
    && getActiveSessionRevision() === cycle.revision && latest.current.sessionBinding?.revision === cycle.revision
    && principal(latest.current.sessionBinding) === cycle.principal && cycle.connection === connection();
  const visible = enabled && !!cycle.principal && revision === cycle.revision
    && sessionBinding?.revision === cycle.revision && principal(sessionBinding) === cycle.principal
    && cycle.connection === connection();
  const writable = () => owns() && latest.current.canManage && cycle.source === 'cloud' && !isDesktopOffline();
  const publish = () => { if (owns()) render(value => value + 1); };
  const configFor = controller => {
    const token = latest.current.getToken?.();
    if (!owns() || !token) throw new Error('Reopen Access Codes from your current session.');
    return { headers: { Authorization: `Bearer ${token}` }, timeout: COMMUNITY_WAIT_MS,
      signal: controller.signal, suppressServerErrorNotification: true };
  };
  const controllerFor = () => { const controller = new AbortController(); cycle.controllers.add(controller); return controller; };
  const readList = async config => {
    const value = await bounded(() => communitiesApi.getCommunities(config), config);
    if (!validList(value)) throw new Error('Invalid community directory');
    return value;
  };
  const readDetail = async (id, config) => {
    const value = await bounded(() => communitiesApi.getCommunity(id, config), config);
    if (!validDetail(value, id)) throw new Error('Invalid community detail');
    return value;
  };
  const refresh = async () => {
    if (!owns() || cycle.busy) return false;
    const listGeneration = ++cycle.listGeneration;
    const detailGeneration = ++cycle.detailGeneration;
    const opening = cycle.opening; const id = cycle.selected;
    const controller = controllerFor();
    const current = () => owns() && opening === cycle.opening;
    cycle.listLoading = true; cycle.listError = ''; cycle.detailError = '';
    cycle.detailLoading = !!id; publish();
    let success = true;
    try {
      const config = configFor(controller);
      await Promise.all([
        readList(config).then(value => {
          if (current() && listGeneration === cycle.listGeneration) cycle.communities = value;
        }).catch(() => { success = false; if (current() && listGeneration === cycle.listGeneration) cycle.listError = 'Communities could not be refreshed. Retry loading.'; }),
        id ? readDetail(id, config).then(value => {
          if (current() && detailGeneration === cycle.detailGeneration) cycle.detail = value;
        }).catch(() => { success = false; if (current() && detailGeneration === cycle.detailGeneration) cycle.detailError = 'Community details could not be loaded. Retry loading.'; }) : Promise.resolve(),
      ]);
      return current() && success;
    } catch {
      if (current()) cycle.listError = 'Communities could not be loaded. Reopen this section.';
      return false;
    } finally {
      cycle.controllers.delete(controller);
      if (current()) {
        if (listGeneration === cycle.listGeneration) cycle.listLoading = false;
        if (detailGeneration === cycle.detailGeneration) cycle.detailLoading = false;
        publish();
      }
    }
  };
  const hasUnsavedWork = () => cycle.pendingPins.length > 0 || cycle.busy || !!cycle.recovery || !!cycle.creationId
    || [...cycle.dirty.values()].some(check => check());
  const canLeave = () => !hasUnsavedWork() || window.confirm('Leave this community workspace and discard unfinished drafts or staged addresses? A submitted request may still finish.');
  const clearSelection = id => {
    cycle.opening += 1; cycle.generation += 1; cycle.detailGeneration += 1;
    cycle.controllers.forEach(controller => controller.abort()); cycle.controllers.clear();
    cycle.selected = id || null; cycle.detail = null; cycle.pendingPins = [];
    cycle.busy = false; cycle.status = 'idle'; cycle.message = ''; cycle.recovery = null; cycle.batch = [];
    cycle.dirty.clear(); cycle.creationId = null; cycle.detailError = ''; cycle.detailLoading = false;
    cycle.ticket = Object.freeze({ cycle, opening: cycle.opening, communityId: cycle.selected });
    publish();
  };
  const selectCommunity = id => {
    if (!owns()) return false;
    if ((id || null) === cycle.selected) return true;
    if (!canLeave()) return false;
    clearSelection(id); refresh(); return true;
  };
  useLayoutEffect(() => {
    cycle.active = enabled;
    if (enabled) refresh();
    return () => { cycle.active = false; cycle.generation += 1; cycle.opening += 1;
      cycle.controllers.forEach(controller => controller.abort()); cycle.controllers.clear(); cycle.dirty.clear(); };
  // Requests belong to this section lifetime, not callback identities.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycle, enabled]);
  useLayoutEffect(() => {
    if ((!visible || !canManage) && cycle.busy) {
      cycle.generation += 1; cycle.controllers.forEach(controller => controller.abort());
      cycle.busy = false; cycle.status = 'retired';
      cycle.message = 'Access changed. Reopen this section to check saved state; a submitted request may still finish.';
      if (visible) render(value => value + 1);
    }
  }, [cycle, visible, canManage]);

  const begin = () => {
    if (!writable() || cycle.busy) return null;
    const controller = controllerFor();
    let config;
    try { config = configFor(controller); } catch { cycle.controllers.delete(controller); return null; }
    const generation = ++cycle.generation; const opening = cycle.opening;
    // Retire background observations before a write, so pre-write reads cannot replace readback.
    cycle.listGeneration += 1; cycle.detailGeneration += 1;
    cycle.listLoading = false; cycle.detailLoading = false; cycle.busy = true; publish();
    return { config, controller, generation, current: () => writable() && opening === cycle.opening && generation === cycle.generation };
  };
  const finish = operation => {
    cycle.controllers.delete(operation.controller);
    if (operation.current()) { cycle.busy = false; publish(); }
    else if (cycle.active && cycle.generation === operation.generation) {
      cycle.busy = false; cycle.status = 'retired';
      cycle.message = 'The connection or access changed. Reopen Access Codes to check saved state; the earlier request may still finish.';
      render(value => value + 1);
    }
  };
  const complete = descriptor => {
    cycle.completedOperation = { sequence: ++cycle.completion, kind: descriptor.kind,
      communityId: descriptor.acceptedCommunityId || descriptor.communityId };
    cycle.recovery = null; cycle.status = 'idle'; cycle.message = 'Saved community information refreshed.';
    if (descriptor.kind === 'create-community') cycle.creationId = null;
  };
  const refreshAfter = async (operation, descriptor) => {
    const id = descriptor.acceptedCommunityId || descriptor.communityId;
    try {
      const [list, detail] = await Promise.all([readList(operation.config),
        id && descriptor.kind !== 'delete-community' ? readDetail(id, operation.config) : Promise.resolve(null)]);
      if (!operation.current()) return false;
      cycle.communities = list;
      if (descriptor.kind === 'create-community') {
        cycle.selected = id; cycle.detail = detail;
        cycle.ticket = Object.freeze({ cycle, opening: cycle.opening, communityId: id });
      } else if (descriptor.kind === 'delete-community') {
        cycle.selected = null; cycle.detail = null; cycle.pendingPins = [];
        cycle.ticket = null;
      } else cycle.detail = detail;
      cycle.listError = ''; cycle.detailError = ''; complete(descriptor); return true;
    } catch {
      if (operation.current()) { cycle.status = 'refresh-required';
        cycle.message = 'The change was saved, but the view could not refresh. Retry refresh only; do not repeat the write.'; }
      return false;
    }
  };
  const execute = async (descriptor, operation) => {
    const { config, current } = operation;
    const call = async (fn, validate) => {
      if (!current()) throw new Error('Retired community operation');
      const ack = await bounded(fn, config);
      if (!current()) throw new Error('Retired community operation');
      if (!validate(ack)) invalidReceipt();
      return ack;
    };
    const id = descriptor.communityId;
    switch (descriptor.kind) {
      case 'create-community': {
        if (!descriptor.acceptedCommunityId) {
          const ack = await call(() => communitiesApi.createCommunity({ name: descriptor.payload.name.trim(), place_ids: [] }, config),
            value => identified(value) && typeof value.name === 'string');
          descriptor.acceptedCommunityId = ack.id;
          cycle.creationId = ack.id;
        }
        if (descriptor.payload.code?.trim() && !descriptor.codeConfirmed) {
          const newId = descriptor.acceptedCommunityId;
          await call(() => createAccessCode({ type: descriptor.payload.type || 'gate', code: descriptor.payload.code.trim(),
            instructions: descriptor.payload.instructions?.trim() || undefined, scope: 'community', community_id: newId }, config),
          value => identified(value) && value.community_id === newId);
          descriptor.codeConfirmed = true;
        }
        break;
      }
      case 'update-community':
        await call(() => communitiesApi.updateCommunity(id, descriptor.payload, config), value => identified(value) && value.id === id && typeof value.name === 'string'); break;
      case 'create-code':
        await call(() => createAccessCode({ ...descriptor.payload, scope: 'community', community_id: id }, config), value => identified(value) && value.community_id === id); break;
      case 'update-code':
        await call(() => updateAccessCode(descriptor.codeId, descriptor.payload, config), value => identified(value) && value.id === descriptor.codeId && value.community_id === id); break;
      case 'delete-code':
        await call(() => deleteAccessCode(descriptor.codeId, config, true), value => value === undefined || value === ''); break;
      case 'remove-address':
        await call(() => communitiesApi.deleteCommunityAddress(id, descriptor.placeId, config), value => value?.removed === true); break;
      case 'delete-community':
        await call(() => communitiesApi.deleteCommunity(id, config), value => value?.id === id && typeof value.already_deleted === 'boolean'); break;
      case 'add-addresses':
        for (const entry of descriptor.batch) {
          if (entry.status === 'confirmed') continue;
          if (!current()) throw new Error('Retired community batch');
          descriptor.activeEntry = entry; entry.status = 'submitting'; publish();
          const pin = entry.pin;
          await call(() => communitiesApi.addCommunityAddress(id, pick(pin, ['place_id', 'formatted_address', 'lat', 'lng']), config),
            value => value?.place_id === pin.place_id && typeof value.already_present === 'boolean');
          entry.status = 'confirmed';
          cycle.pendingPins = cycle.pendingPins.filter(candidate => candidate.tempId !== pin.tempId);
          descriptor.activeEntry = null; publish();
        }
        break;
      default: throw new Error('Unsupported operation');
    }
    descriptor.acknowledged = true;
  };
  const mutate = async (descriptor, retry = false) => {
    if (!writable() || cycle.busy || !(retry ? ['rejected', 'checked'] : ['idle']).includes(cycle.status)) return { ok: false, saved: false };
    if (descriptor.kind !== 'create-community' && (!cycle.detail || descriptor.communityId !== cycle.selected)) return { ok: false, saved: false };
    const operation = begin(); if (!operation) return { ok: false, saved: false };
    cycle.recovery = descriptor; cycle.message = 'Saving community information...';
    if (descriptor.batch) cycle.batch = descriptor.batch;
    try {
      try { await execute(descriptor, operation); }
      catch (error) {
        if (!operation.current()) return { ok: false, saved: false };
        const httpStatus = error?.response?.status;
        const rejected = httpStatus >= 400 && httpStatus < 500 && httpStatus !== 408;
        cycle.status = rejected ? 'rejected' : 'unknown';
        if (descriptor.activeEntry) descriptor.activeEntry.status = rejected ? 'rejected' : 'unknown';
        const partial = descriptor.acceptedCommunityId ? 'The community was created; its optional code was not confirmed. ' : '';
        const reason = typeof error?.response?.data?.detail === 'string' ? `${error.response.data.detail} ` : '';
        cycle.message = partial + (rejected
          ? `${reason}The remaining change was rejected. Your draft and confirmed progress are retained. Revise the draft or retry deliberately when ready.`
          : 'The result is uncertain; the submitted request may still finish. Check saved state before deciding whether to retry.');
        return { ok: false, saved: !!descriptor.acceptedCommunityId, communityId: descriptor.acceptedCommunityId };
      }
      const ok = await refreshAfter(operation, descriptor);
      return { ok, saved: true, communityId: descriptor.acceptedCommunityId || descriptor.communityId };
    } finally { finish(operation); }
  };
  const checkSavedState = async () => {
    if (renderedOpening !== cycle.opening || !['unknown', 'checked', 'rejected'].includes(cycle.status) || !cycle.recovery) return false;
    const operation = begin(); if (!operation) return false;
    const descriptor = cycle.recovery;
    try {
      const id = descriptor.acceptedCommunityId || descriptor.communityId;
      const [list, detail] = await Promise.all([readList(operation.config), id ? readDetail(id, operation.config).catch(error => {
        if (error?.response?.status === 404 && descriptor.kind === 'delete-community') return null;
        throw error;
      }) : Promise.resolve(null)]);
      if (!operation.current()) return false;
      cycle.communities = list;
      if (id === cycle.selected) cycle.detail = detail;
      descriptor.observedDetail = detail;
      cycle.status = 'checked';
      cycle.message = 'Current saved records are available for review. This does not prove which request created them, and an earlier request may still finish. Accept the current state or deliberately retry remaining work.';
      return true;
    } catch {
      if (operation.current()) { cycle.message = 'Saved state could not be checked. Check again before retrying an uncertain write.';
        if (cycle.status !== 'rejected') cycle.status = 'unknown'; }
      return false;
    } finally { finish(operation); }
  };
  const retryRefresh = async () => {
    if (renderedOpening !== cycle.opening || cycle.status !== 'refresh-required' || !cycle.recovery) return false;
    const operation = begin(); if (!operation) return false;
    try { return await refreshAfter(operation, cycle.recovery); } finally { finish(operation); }
  };
  const retryMutation = () => {
    if (renderedOpening !== cycle.opening || !cycle.recovery || !['checked', 'rejected'].includes(cycle.status)) return Promise.resolve({ ok: false, saved: false });
    if (cycle.status === 'checked' && !window.confirm('The earlier request may still finish. Retry the remaining unconfirmed work anyway? Creating a community or code again can create duplicates.')) return Promise.resolve({ ok: false, saved: false });
    return mutate(cycle.recovery, true);
  };
  const acceptSavedState = () => {
    if (renderedOpening !== cycle.opening || !writable() || cycle.busy || cycle.status !== 'checked' || !cycle.recovery) return false;
    const descriptor = cycle.recovery;
    // Explicit acceptance does not invent an ID for unknown creation or claim receipt causality.
    if (descriptor.acceptedCommunityId && descriptor.observedDetail) {
      cycle.selected = descriptor.acceptedCommunityId; cycle.detail = descriptor.observedDetail;
      cycle.ticket = Object.freeze({ cycle, opening: cycle.opening, communityId: cycle.selected });
    }
    if (descriptor.kind === 'delete-community' && !descriptor.observedDetail) {
      cycle.selected = null; cycle.detail = null; cycle.pendingPins = []; cycle.ticket = null;
    }
    if (descriptor.batch) {
      const present = new Set((descriptor.observedDetail?.addresses || []).map(row => row.place_id));
      cycle.pendingPins = cycle.pendingPins.filter(pin => !present.has(pin.place_id));
    }
    complete(descriptor); cycle.message = 'Current saved state accepted. An earlier request may still finish.'; publish(); return true;
  };
  const captureSelection = () => {
    if (!writable() || !cycle.selected) return null;
    if (!cycle.ticket) cycle.ticket = Object.freeze({ cycle, opening: cycle.opening, communityId: cycle.selected });
    return cycle.ticket;
  };
  const isCurrent = ticket => !!ticket && ticket.cycle === cycle && ticket.opening === cycle.opening
    && ticket.communityId === cycle.selected && writable();
  const addPendingPin = (pin, ticket) => {
    if (!isCurrent(ticket) || cycle.busy || cycle.status !== 'idle' || !pin?.place_id
      || cycle.pendingPins.some(row => row.place_id === pin.place_id)
      || cycle.detail?.addresses.some(row => row.place_id === pin.place_id)) return false;
    cycle.pendingPins = [...cycle.pendingPins, { ...pick(pin, ['place_id', 'formatted_address', 'lat', 'lng']),
      tempId: `community-${cycle.opening}-${++cycle.pinSequence}` }]; publish(); return true;
  };
  const action = (kind, extra = {}) => renderedOpening === cycle.opening && renderedSelection === cycle.selected
    ? mutate({ kind, communityId: cycle.selected, ...extra }) : Promise.resolve({ ok: false, saved: false });
  const createCommunity = payload => {
    if (renderedOpening !== cycle.opening || renderedSelection !== cycle.selected || !writable() || cycle.busy || cycle.status !== 'idle') return Promise.resolve({ ok: false, saved: false });
    if (cycle.selected) {
      if (!canLeave()) return Promise.resolve({ ok: false, saved: false });
      clearSelection(null);
    }
    return mutate({ kind: 'create-community', communityId: null, acceptedCommunityId: cycle.creationId,
      payload: pick(payload, ['name', 'type', 'code', 'instructions']) });
  };
  const reviseRejected = () => {
    if (renderedOpening !== cycle.opening || !writable() || cycle.busy || cycle.status !== 'rejected') return false;
    cycle.recovery = null; cycle.status = 'idle';
    cycle.message = cycle.creationId ? 'The community already exists. Edit its optional code; submitting will not recreate the community.'
      : 'Your draft and remaining staged addresses are retained for revision.';
    publish(); return true;
  };
  return {
    communities: visible ? cycle.communities : [], selectedCommunityId: visible ? cycle.selected : null,
    detail: visible ? cycle.detail : null, opening: cycle.opening,
    listLoading: visible && cycle.listLoading, detailLoading: visible && cycle.detailLoading,
    listError: visible ? cycle.listError : enabled ? 'The connection or session changed. Reopen Access Codes.' : '',
    detailError: visible ? cycle.detailError : '', pendingPins: visible ? cycle.pendingPins : [],
    busy: visible && cycle.busy, status: visible ? cycle.status : 'retired', message: visible ? cycle.message : '',
    recovery: visible ? cycle.recovery : null, batch: visible ? cycle.batch : [], completedOperation: visible ? cycle.completedOperation : null,
    acceptedCommunityId: visible ? cycle.creationId : null,
    canManage, canMutate: visible && writable() && !cycle.busy && cycle.status === 'idle',
    canRecover: visible && writable() && !cycle.busy && cycle.status !== 'retired',
    readOnly: !visible || !writable() || cycle.busy || cycle.status !== 'idle',
    hasUnsavedWork: visible && hasUnsavedWork(), canLeave, refresh, selectCommunity,
    resetSelection: () => { if (owns()) clearSelection(null); },
    registerDirty: (key, check) => { cycle.dirty.set(key, check); return () => { if (cycle.dirty.get(key) === check) cycle.dirty.delete(key); }; },
    captureSelection, isCurrent, addPendingPin,
    removePendingPin: tempId => { if (!writable() || cycle.busy || cycle.status !== 'idle') return;
      cycle.pendingPins = cycle.pendingPins.filter(pin => pin.tempId !== tempId); publish(); },
    createCommunity,
    updateCommunity: payload => action('update-community', { payload: pick(payload, ['name', 'guard_gated']) }),
    createCode: payload => action('create-code', { payload: pick(payload, ['type', 'code', 'instructions']) }),
    updateCode: (codeId, payload) => action('update-code', { codeId, payload: pick(payload, ['code', 'instructions', 'deprecated', 'deprecated_reason']) }),
    deleteCode: codeId => action('delete-code', { codeId }), removeAddress: placeId => action('remove-address', { placeId }),
    deleteCommunity: () => action('delete-community'),
    addAllPending: () => cycle.pendingPins.length ? action('add-addresses', { batch: cycle.pendingPins.map(pin => ({ pin: { ...pin }, status: 'pending' })) }) : Promise.resolve({ ok: false, saved: false }),
    checkSavedState, retryMutation, retryRefresh, acceptSavedState, reviseRejected,
  };
}
